import React, { useEffect, useState } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
// import ReactHtmlParser from 'html-react-parser';
import {
    Box, Paper, Typography, IconButton, Tooltip, Stack,
    Grid,
    FormControlLabel,
    Switch,
    alpha,
} from '@mui/material'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { ReactComponent as DiscreteIcon } from '../../../assets/sensors/discrete.svg'
import { ReactComponent as MeasurementIcon } from '../../../assets/sensors/centimeter.svg'
import { ReactComponent as OtherSensorIcon } from '../../../assets/sensors/sensor.svg'
import { ReactComponent as AggregateIcon } from '../../../assets/sensors/chip.svg'
import { ReactComponent as DecoderIcon } from '../../../assets/sensors/decoder.svg'
import { ReactComponent as ParameterIcon } from '../../../assets/nav/algorithm.svg'
import { checkPrivileges } from '../../../Helpers'
import instance from '../../../axios'
import { notifyError, notifySuccess } from '../../../Utils'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/material/styles';

const IOSSwitch = styled((props) => (
  <Switch
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ themecolors }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',

      '& .MuiSwitch-thumb': {
        color: themecolors?.textColor || '#fff',
      },

      '& + .MuiSwitch-track': {
        backgroundColor: themecolors?.backgroundColor || '#65C466',
        opacity: 1,
      },
    },

    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#BDBDBD',
    },

    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
    },
  },

  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    color: '#BDBDBD',
  },

  '& .MuiSwitch-track': {
    borderRadius: 13,
    backgroundColor: alpha(themecolors?.backgroundColor || '#E9E9EA', 0.9),
    transition: 'background-color 300ms',
  },
}));

const SensorTable = ({
    computedAttributesDeviceWise,
    translate,
    onCopy,
    onEdit,
    onRemove,
    fetchComputedAttributeSensorById,
    onSave,
    themecolors
}) => {
    const dispatch=useDispatch()
    const [sensorData, setSensorData] = useState([]);

    useEffect(() => {
        const sortedSensorRows = [...computedAttributesDeviceWise]
            .sort((a, b) => a.priority - b.priority)
            .map(sensor => ({
                id: `sensor-${sensor.id}`,
                data: sensor,
            }));
        setSensorData(sortedSensorRows);
    }, [computedAttributesDeviceWise]);

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination || source.index === destination.index) return;

        const updated = [...sensorData];
        const [movedItem] = updated.splice(source.index, 1);
        updated.splice(destination.index, 0, movedItem);
        setSensorData(updated);

        const dataToSend = updated.reduce((acc, item, index) => {
            acc[item.data?.id] = index;
            return acc;
        }, {});
        updateList(dataToSend)
    }
    const updateList = async (data) => {
        try {
            await instance.post('/api/sensors/priority', data);
            fetchComputedAttributeSensorById();
            dispatch(notifySuccess("Sensor sequence updated successfully"))
        } catch (error) {
            dispatch(notifyError(error?.message || "Something went wrong"))
        }
    };


    const getCategory = (category) => {
        switch (category) {
            case 1: return 'discrete';
            case 2: return 'measurement';
            case 3: return 'others';
            case 4: return 'aggregate'
            case -1: return 'decoder';
            case 0: return 'elogic';
            default: return 'other';
        }
    };
    const handleEditSensor = (item) => {
        if (item && onEdit) {
            const mood = getCategory(item?.category)
            onEdit(item, mood)
        }
    }
    return (
        <Box sx={{}}>
            <SensorListHeader translate={translate} themecolors={themecolors}/>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="SensorTable">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                maxHeight: 500,
                                overflowY: 'auto',
                                // pr: 1,
                            }}
                        >
                            <Stack spacing={1}>
                                {sensorData.map((row, index) => {
                                    const sensor = row.data;
                                    return (
                                        <Draggable key={row.id} draggableId={row.id} index={index}>
                                            {(provided, snapshot) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <SensorListItem
                                                        sensor={sensor}
                                                        translate={translate}
                                                        onCopy={() => onCopy(sensor, 'copySensor')}
                                                        onEdit={() => handleEditSensor(sensor)}
                                                        onRemove={() => onRemove(sensor)}
                                                        onSave={()=>onSave(sensor)}
                                                        dragHandleProps={provided.dragHandleProps}
                                                        isDragging={snapshot.isDragging}
                                                        themecolors={themecolors}
                                                    />
                                                </Box>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </Stack>
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    );
};

const SensorListItem = ({
    sensor,
    translate,
    onEdit,
    onCopy,
    onRemove,
    dragHandleProps,
    isDragging,
    onSave,
    themecolors
}) => {
    const getCategoryTitle = (category) => {
        switch (category) {
            case 1: return translate?.('discrete');
            case 2: return translate?.('measurement');
            case 3: return translate?.('otherSensors');
            case 4: return translate?.('aggregateSensors');
            case 0: return translate?.('elogic');
            case -1: return translate?.('decoderSensors');
            default: return translate?.('other');
        }
    };
    const getCategoryIcon = (category) => {
        switch (category) {
            case 1: return <DiscreteIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            case 2: return <MeasurementIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            case 3: return <OtherSensorIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            case 4: return <AggregateIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            case 0: return <ParameterIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            case -1: return <DecoderIcon fill='currentColor' style={{ width: 20, height: 20, marginRight: 8 }} />;
            default: return null;
        }
    };
    const cleanAttribute = sensor?.attribute?.replace?.(/[0-9]/g, '') ?? '';
    const key = `sensorsTranslation.${cleanAttribute}`;
    const label = translate?.(key);
    const attributeLabel = label !== key && label ? label : sensor?.attribute || '-';

    return (
        <Paper
            sx={{
                backgroundColor:`${themecolors?.themeLightColor} !important`,
                color: themecolors?.themeLightInverse || '#fff',
                p: 1,
                borderRadius: '0.5rem !important',
                border: isDragging ? '1px dashed #90caf9' : '1px solid transparent',
                width: '100%',
                overflow: 'hidden',
            }}
        >
            <Grid
                container
                alignItems="center"
                spacing={1}
                flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                sx={{
                    width: '100%',
                }}
            >
                <Grid item xs={1} order={1} sx={{ display: 'flex', justifyContent: 'center' }} {...dragHandleProps}>
                    <DragIndicatorIcon sx={{ color:themecolors?.themeLightInverse|| '#fff', cursor: 'grab' }} />
                </Grid>
                <Grid item xs={5} sm={1.5} order={5} sx={{ display: 'flex', alignItems: 'center', gap: 1, color:themecolors?.themeLightInverse|| '#e0f7fa', }}>
                    <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        {getCategoryTitle(sensor?.category)}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2} order={{ xs: 5, sm: 3 }}>
                    <Typography
                        variant="body2"
                        noWrap
                        textAlign="start"
                        sx={{ color:themecolors?.themeLightInverse|| '#e0f7fa', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {attributeLabel}
                    </Typography>
                </Grid>
                <Grid item xs={3} sm={2} order={{ xs: 6, sm: 4 }}>
                    <Typography
                        variant="body2"
                        noWrap
                        textAlign="start"
                        sx={{ color:themecolors?.themeLightInverse|| '#e0f7fa', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {sensor?.attributes?.input ?? '-'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={3} order={{ xs: 7, sm: 2 }} sx={{ display: 'flex', alignItems: 'center', gap: 0.1, color:themecolors?.themeLightInverse|| '#e0f7fa', }}>
                    {getCategoryIcon(sensor?.category)}
                    <Typography
                        variant="body2"
                        noWrap
                        fontWeight="bold"
                        textAlign="start"
                        sx={{ color:themecolors?.themeLightInverse|| '#e0f7fa', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {sensor?.description ?? '-'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2.5} order={{ xs: 8, sm: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
<FormControlLabel
  control={
    <IOSSwitch
      checked={sensor?.attributes?.isMonitoring ?? false}
      name="showMonitoring"
      onChange={onSave}
      themecolors={themecolors}
    />
  }
  title={`Click to ${sensor?.attributes?.isMonitoring ? "Hide" : "Show"}`}
  label={sensor?.attributes?.isMonitoring ? "Hide" : "Show"}
  sx={{
    marginLeft: 1,
    '& .MuiFormControlLabel-label': {
      marginLeft: 1,
    color:themecolors?.themeLightInverse|| '#e0f7fa',
    },
  }}
/>

</Grid>

                
                <Grid item xs={6} sm={1.5} order={{ xs: 4, sm: 7 }} sx={{ display: 'flex', gap:0.2, justifyContent: 'flex-end' }}>
                    {checkPrivileges("attributeCreate") && <Tooltip title={translate?.('copySensor')}>
                        <IconButton onClick={onCopy} size="small" sx={{ color:themecolors?.themeLightInverse|| '#fff' }}>
                            <FileCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>}
                    {checkPrivileges("attributeUpdate") &&
                        <Tooltip title={translate?.('edit')}>
                            <IconButton onClick={onEdit} size="small" sx={{ color:themecolors?.themeLightInverse|| '#fff' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        checkPrivileges("attributeDelete") &&
                        <Tooltip title={translate?.('delete')}>
                            <IconButton onClick={onRemove} size="small" sx={{ color:themecolors?.themeLightInverse|| '#fff' }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                </Grid>
            </Grid>
        </Paper>
    );
};
const SensorListHeader = ({ translate,themecolors }) => {
    return (
        <Paper
            sx={{
                backgroundColor: themecolors?.themeListingColor ? themecolors.themeListingColor + '77 !important' : '#0A83A1 !important',
                color:themecolors?.themeLightInverse|| '#fff',
                p: 1,
                borderRadius: '0.5rem !important',
                border: '1px solid transparent !important',
                width: '100%',
                overflow: 'hidden',
                mb: 1
            }}
        >
            <Grid
                container
                alignItems="center"
                spacing={1}
                flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                sx={{ width: '100%' }}
            >
                <Grid item xs={1} order={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" textAlign="center">
                        #
                    </Typography>
                </Grid>
                <Grid item xs={5} sm={3} order={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                        {translate?.('label') ?? 'Label'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2} order={{ xs: 5, sm: 3 }}>
                    <Typography variant="body2" fontWeight="bold" textAlign="start" noWrap>
                        {translate?.('sensorType') ?? 'Sensor Type'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2} order={{ xs: 6, sm: 4 }}>
                    <Typography variant="body2" fontWeight="bold" textAlign="start" noWrap>
                        {translate?.('input') ?? 'Input'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={1.5} order={{ xs: 7, sm: 5 }}>
                    <Typography variant="body2" fontWeight="bold" textAlign="start" noWrap>
                        {translate?.('sensor') ?? 'Sensor'}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2.5} order={{ xs: 8, sm: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" textAlign="start" noWrap>
                        {translate?.('showMonitoring') ?? 'Monitoring'}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={1.5} order={{ xs: 4, sm: 7 }} sx={{ display: 'flex', justifyContent: 'flex-end',paddingRight:3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" textAlign="start" noWrap>
                        {translate?.('actions') ?? 'Actions'}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};




export default SensorTable
